import { UseQueryResult } from "@tanstack/react-query";
import {
  ColumnDef,
  getCoreRowModel,
  OnChangeFn,
  PaginationState,
  Table,
  TableOptions,
  useReactTable,
} from "@tanstack/react-table";
import {
  ServerResourceListResult,
  useServerResourceList,
  UseServerResourceListProps,
} from "hooks/server-table/useServerResourceList";
import React, { ReactNode, useContext } from "react";

type TableContextValue = {
  table: Table<unknown>;
  queryResult?: UseQueryResult<ServerResourceListResult<unknown>>;
};

interface ServerDynamicTableProviderProps<TData> {
  columns: ColumnDef<TData>[];
  resource: UseServerResourceListProps<TData>;
  children?: ReactNode;
  pagination: PaginationState;
  globalFilter: string;
  onPaginationChange: OnChangeFn<PaginationState>;
  onGlobalFilterChange: (value: string) => void;
  tableOptions?: Partial<TableOptions<TData>>;
}

interface ServerTableProviderProps<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  rowCount: number;
  children?: ReactNode;
  queryResult?: UseQueryResult<ServerResourceListResult<TData>>;
  pagination: PaginationState;
  globalFilter: string;
  onPaginationChange: OnChangeFn<PaginationState>;
  onGlobalFilterChange: (value: string) => void;
  tableOptions?: Partial<TableOptions<TData>>;
}

const ServerDynamicTableContext = React.createContext<TableContextValue | undefined>(
  undefined,
);

export const useServerDynamicTableContext = () => {
  const value = useContext(ServerDynamicTableContext);
  if (value === undefined) {
    throw Error(
      "useServerDynamicTableContext can not be used outside a ServerDynamicTableProvider",
    );
  }
  return value;
};

export default function ServerDynamicTableProvider<TData>({
  columns,
  resource,
  tableOptions,
  children,
  pagination,
  globalFilter,
  onPaginationChange,
  onGlobalFilterChange,
}: ServerDynamicTableProviderProps<TData>) {
  const queryResult = useServerResourceList<TData>({ ...resource });
  const { data } = queryResult;

  return (
    <ServerTableProvider<TData>
      columns={columns}
      data={data?.items ?? []}
      rowCount={data?.meta.total ?? 0}
      tableOptions={tableOptions}
      queryResult={queryResult}
      pagination={pagination}
      globalFilter={globalFilter}
      onPaginationChange={onPaginationChange}
      onGlobalFilterChange={onGlobalFilterChange}
    >
      {children}
    </ServerTableProvider>
  );
}

function ServerTableProvider<TData>({
  columns,
  data,
  rowCount,
  queryResult,
  tableOptions = {},
  children,
  pagination,
  globalFilter,
  onPaginationChange,
  onGlobalFilterChange,
}: ServerTableProviderProps<TData>) {
  const options: TableOptions<TData> = {
    columns,
    data,
    rowCount,
    manualPagination: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    globalFilterFn: "includesString",
    state: {
      pagination,
      globalFilter,
    },
    onPaginationChange,
    onGlobalFilterChange,
    ...tableOptions,
  };

  const table = useReactTable(options);

  return (
    <ServerDynamicTableContext.Provider
      value={{
        table,
        queryResult,
      }}
    >
      {children}
    </ServerDynamicTableContext.Provider>
  );
}
